import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, FileText, Lock, Send, Hand, AlertTriangle, Check, X, RefreshCw,
  Gavel, Coins, ExternalLink, Activity,
} from "lucide-react";
import { contractsApi, formatApiError } from "../../lib/ledgerApi";
import { useAuth } from "../../contexts/AuthContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import ContractStateBadge from "../../components/ContractStateBadge";

const fmt = (s) => {
  try {
    const n = Number(s);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : s;
  } catch { return s; }
};

const STATE_FLOW = ["draft", "active", "fulfilled"];

function StateTimeline({ contract }) {
  const states = ["draft", "active", contract.state === "refunded" ? "refunded"
    : contract.state === "cancelled" ? "cancelled"
    : contract.state === "disputed" ? "disputed"
    : contract.state === "blocked" ? "blocked"
    : contract.state === "expired" ? "expired"
    : "fulfilled"];
  const reachedIdx = states.indexOf(contract.state);
  return (
    <div className="flex items-center gap-2 my-2" data-testid="contract-timeline">
      {states.map((s, i) => {
        const reached = i <= reachedIdx;
        return (
          <React.Fragment key={s}>
            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-sm font-mono ${reached ? "border-foreground text-foreground" : "border-border text-muted-foreground"}`}>
              {s}
            </span>
            {i < states.length - 1 && (
              <span className={`flex-1 h-px ${reached && i + 1 <= reachedIdx ? "bg-foreground" : "bg-border"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function ContractDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { business, user } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveDecision, setResolveDecision] = useState("release");
  const [resolveNote, setResolveNote] = useState("");
  const [flagName, setFlagName] = useState("");

  const load = async () => {
    try {
      const r = await contractsApi.get(id);
      setC(r);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, [id]); // eslint-disable-line

  if (loading || !c) {
    return <div className="max-w-5xl px-6 md:px-10 py-10 text-sm text-muted-foreground">Loading…</div>;
  }

  const myBid = business?.id;
  const isCreator = c.creator_business_id === myBid;
  const isCounterparty = c.counterparty_business_id === myBid;
  const isArbiter = c.arbiter_business_id === myBid;
  const isSuperAdmin = user?.role === "super_admin";
  const remaining = (Number(c.total_locked_amount) - Number(c.released_amount));

  const actLargeAmount = (() => {
    // Approx threshold check — server is source of truth, but show the phrase challenge for clarity
    return Number(c.total_locked_amount) >= 5000 && c.currency === "USD";
  })();
  const expectedPhrase = `YES LOCK ${c.total_locked_amount} ${c.currency} TO ${(c.counterparty_legal_name || "").toUpperCase().replace(/[^A-Z]/g, "")}@CONTRACT`.replace(/\s+/g, " ");
  // Use actual VPA-derived phrase from spec
  const counterpartyVpa = c.spec?.recipient_vpa || (c.counterparty_legal_name || "");

  const onActivate = async () => {
    setBusy(true);
    try {
      const r = await contractsApi.activate(c.id, confirmText || null);
      setActivateOpen(false);
      setConfirmText("");
      setC(r);
      toast.success("Contract activated", { description: `Locked ${c.total_locked_amount} ${c.currency}` });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "LARGE_AMOUNT_REQUIRES_CONFIRMATION") {
        toast.warning("Phrase challenge required", { description: detail.message });
      } else if (detail?.code === "BLOCKED_BY_RISK") {
        toast.error("Blocked by risk engine", { description: detail.message });
        setActivateOpen(false);
      } else if (detail?.code === "HELD_FOR_REVIEW") {
        toast.warning("Activation held for review", { description: detail.message });
        setActivateOpen(false);
      } else {
        toast.error("Activate failed", { description: formatApiError(detail) });
      }
    } finally {
      setBusy(false);
    }
  };

  const wrap = (fn, label) => async () => {
    setBusy(true);
    try {
      const r = await fn();
      setC(r);
      toast.success(label);
    } catch (e) {
      toast.error(`${label} failed`, { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const onResolve = async () => {
    setBusy(true);
    try {
      const r = await contractsApi.resolve(c.id, resolveDecision, resolveNote || null);
      setResolveOpen(false);
      setC(r);
      toast.success(`Dispute resolved → ${resolveDecision}`);
    } catch (e) {
      toast.error("Resolve failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const onSetFlag = async () => {
    if (!flagName) return;
    setBusy(true);
    try {
      const r = await contractsApi.setFlag(c.id, flagName, true);
      setC(r);
      toast.success(`Flag '${flagName}' set`);
      setFlagName("");
    } catch (e) {
      toast.error("Set flag failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14" data-testid="contract-detail">
      <Link to="/app/contracts" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to contracts
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ {c.contract_type} contract</div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-medium font-mono break-all" data-testid="contract-id">{c.id}</h1>
        </div>
        <ContractStateBadge state={c.state} size="lg" />
      </div>

      <StateTimeline contract={c} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Parties + amounts */}
          <div className="border border-border rounded-sm bg-card p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Parties</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Creator</div>
                <div className="font-medium" data-testid="contract-creator">{c.creator_legal_name}</div>
                {isCreator && <div className="text-[10px] text-status-info uppercase tracking-widest mt-0.5">you</div>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Counterparty</div>
                <div className="font-medium" data-testid="contract-counterparty">{c.counterparty_legal_name}</div>
                {isCounterparty && <div className="text-[10px] text-status-info uppercase tracking-widest mt-0.5">you</div>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Arbiter</div>
                <div className="font-medium" data-testid="contract-arbiter">{c.arbiter_legal_name || "—"}</div>
                {isArbiter && <div className="text-[10px] text-status-info uppercase tracking-widest mt-0.5">you</div>}
              </div>
            </div>
          </div>

          {/* Amounts */}
          <div className="border border-border rounded-sm bg-card p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Funds</div>
            <div className="grid grid-cols-3 gap-3 text-sm font-mono">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Locked</div>
                <div className="text-xl">{fmt(c.total_locked_amount)} <span className="text-muted-foreground text-sm">{c.currency}</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Released</div>
                <div className="text-xl text-status-ok">{fmt(c.released_amount)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Remaining</div>
                <div className="text-xl">{fmt(remaining)}</div>
              </div>
            </div>
            {c.contract_address && (
              <div className="text-[10px] font-mono text-muted-foreground mt-3 break-all">
                escrow address: {c.contract_address}
              </div>
            )}
          </div>

          {/* Spec */}
          <div className="border border-border rounded-sm bg-card p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Spec</div>
            <pre className="text-[11px] font-mono text-muted-foreground overflow-x-auto whitespace-pre-wrap" data-testid="contract-spec">
{JSON.stringify(c.spec, null, 2)}
            </pre>
          </div>

          {/* Conditions for conditional contracts */}
          {c.contract_type === "conditional" && c.spec?.conditions?.length > 0 && (
            <div className="border border-border rounded-sm bg-card p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Conditions ({c.spec.logic} of)</div>
              <div className="space-y-2">
                {c.spec.conditions.map((cond, i) => (
                  <div key={i} data-testid={`cond-${i}`} className={`p-3 border rounded-sm flex items-center gap-2 ${cond.satisfied ? "border-status-ok/40 bg-status-ok/5" : "border-border bg-muted/30"}`}>
                    {cond.satisfied
                      ? <Check className="w-4 h-4 text-status-ok shrink-0" strokeWidth={1.5} />
                      : <Activity className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />}
                    <div className="font-mono text-xs flex-1 break-all">
                      <span className="uppercase tracking-widest mr-2">{cond.type}</span>
                      {cond.name && <span>{cond.name}</span>}
                      {cond.oracle_name && <span>{cond.oracle_name} = {cond.expected_value}</span>}
                      {cond.when && <span>{cond.when}</span>}
                      {cond.invoice_id && <span>{cond.invoice_id.slice(0, 12)}…</span>}
                    </div>
                    {cond.satisfied_at && (
                      <span className="text-[10px] text-muted-foreground font-mono">{new Date(cond.satisfied_at).toLocaleString()}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History */}
          <div className="border border-border rounded-sm bg-card p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">History</div>
            <ul className="space-y-2 text-xs">
              {c.history.map((h, i) => (
                <li key={i} className="flex items-start gap-3 font-mono">
                  <span className="text-muted-foreground w-44 shrink-0">{new Date(h.at).toLocaleString()}</span>
                  <span className="font-medium">{h.action}</span>
                  {h.from_state && h.to_state && h.from_state !== h.to_state && (
                    <span className="text-muted-foreground">{h.from_state} → {h.to_state}</span>
                  )}
                  <span className="text-muted-foreground ml-auto truncate">{h.actor}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Actions sidebar */}
        <div className="lg:col-span-1 space-y-3">
          <div className="border border-border rounded-sm bg-card p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Actions</div>
            {c.state === "draft" && isCreator && (
              <Button onClick={() => setActivateOpen(true)} disabled={busy} data-testid="action-activate" className="w-full rounded-sm mb-2">
                <Lock className="w-3 h-3 mr-1" strokeWidth={1.5} /> Sign & activate
              </Button>
            )}
            {c.state === "draft" && isCreator && (
              <Button variant="outline" onClick={wrap(() => contractsApi.cancel(c.id), "Cancelled")} disabled={busy} data-testid="action-cancel-draft" className="w-full rounded-sm">
                <X className="w-3 h-3 mr-1" strokeWidth={1.5} /> Cancel draft
              </Button>
            )}
            {c.state === "active" && c.contract_type === "escrow" && isCounterparty &&
             c.spec?.release_condition === "recipient_confirmation" && (
              <Button onClick={wrap(() => contractsApi.confirm(c.id), "Delivery confirmed")} disabled={busy} data-testid="action-confirm" className="w-full rounded-sm mb-2">
                <Check className="w-3 h-3 mr-1" strokeWidth={1.5} /> Confirm delivery
              </Button>
            )}
            {c.state === "active" && c.contract_type === "escrow" && (isCreator || isCounterparty) && (
              <Button variant="outline" onClick={wrap(() => contractsApi.dispute(c.id), "Dispute raised")} disabled={busy} data-testid="action-dispute" className="w-full rounded-sm mb-2 hover:border-amber-500 hover:text-amber-600 dark:hover:text-amber-400">
                <AlertTriangle className="w-3 h-3 mr-1" strokeWidth={1.5} /> Raise dispute
              </Button>
            )}
            {c.state === "disputed" && isArbiter && (
              <Button onClick={() => setResolveOpen(true)} disabled={busy} data-testid="action-resolve" className="w-full rounded-sm mb-2">
                <Gavel className="w-3 h-3 mr-1" strokeWidth={1.5} /> Resolve dispute
              </Button>
            )}
            {c.state === "active" && c.contract_type === "streaming" && isCounterparty && (
              <Button onClick={wrap(() => contractsApi.claim(c.id), "Claimed")} disabled={busy} data-testid="action-claim" className="w-full rounded-sm mb-2">
                <Coins className="w-3 h-3 mr-1" strokeWidth={1.5} /> Claim accrued
              </Button>
            )}
            {c.state === "active" && (isCreator || isCounterparty) && (
              <Button variant="outline" onClick={wrap(() => contractsApi.cancel(c.id), "Cancelled & refunded")} disabled={busy} data-testid="action-cancel-active" className="w-full rounded-sm hover:border-status-tampered hover:text-status-tampered">
                <X className="w-3 h-3 mr-1" strokeWidth={1.5} /> Cancel & refund
              </Button>
            )}
            {c.state === "active" && c.contract_type === "conditional" && (
              <div className="space-y-2 mt-3 pt-3 border-t border-border">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Set manual flag</div>
                <Input
                  data-testid="flag-name"
                  value={flagName}
                  onChange={(e) => setFlagName(e.target.value)}
                  placeholder="flag name"
                  className="rounded-sm font-mono text-xs"
                />
                <Button onClick={onSetFlag} disabled={busy || !flagName} data-testid="action-set-flag" size="sm" className="w-full rounded-sm">
                  Set flag
                </Button>
              </div>
            )}
            {!isCreator && !isCounterparty && !isArbiter && (
              <div className="text-xs text-muted-foreground">No actions available — viewing as super-admin.</div>
            )}
          </div>

          {/* Ledger txns */}
          {(c.ledger_lock_tx_id || c.ledger_release_tx_ids?.length > 0) && (
            <div className="border border-border rounded-sm bg-card p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Ledger</div>
              {c.ledger_lock_tx_id && (
                <Link to={`/app/tx/${c.ledger_lock_tx_id}`} className="flex items-center gap-1 text-xs hover:underline" data-testid="lock-tx-link">
                  <Lock className="w-3 h-3" strokeWidth={1.5} /> lock tx <span className="font-mono">{c.ledger_lock_tx_id.slice(0, 16)}…</span>
                  <ExternalLink className="w-3 h-3 ml-auto" strokeWidth={1.5} />
                </Link>
              )}
              {c.ledger_release_tx_ids?.map((t) => (
                <Link key={t} to={`/app/tx/${t}`} className="flex items-center gap-1 text-xs hover:underline mt-1" data-testid={`release-tx-${t}`}>
                  <Send className="w-3 h-3" strokeWidth={1.5} /> release <span className="font-mono">{t.slice(0, 16)}…</span>
                  <ExternalLink className="w-3 h-3 ml-auto" strokeWidth={1.5} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activate dialog */}
      <Dialog open={activateOpen} onOpenChange={(o) => { if (!o) setActivateOpen(false); }}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Sign & activate contract</DialogTitle>
            <DialogDescription>
              This will lock <span className="font-mono">{c.total_locked_amount} {c.currency}</span> from your wallet
              into a contract-controlled escrow address. The activation tx is irreversible.
              The fraud + compliance pipeline runs server-side.
            </DialogDescription>
          </DialogHeader>
          {actLargeAmount && (
            <div className="space-y-2 text-xs">
              <div className="text-amber-600 dark:text-amber-400">
                Large amount detected — type the phrase to authorise:
              </div>
              <div className="font-mono bg-muted p-2 rounded-sm select-all text-[11px]">
                YES LOCK {c.total_locked_amount} {c.currency} TO {(c.spec?.recipient_vpa || c.counterparty_legal_name || "").toUpperCase().split("@")[0]}@CONTRACT
              </div>
              <Input
                data-testid="activate-confirm-phrase"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type phrase exactly…"
                className="rounded-sm font-mono"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivateOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={onActivate} disabled={busy} data-testid="activate-confirm" className="rounded-sm">
              <Lock className="w-3 h-3 mr-1" strokeWidth={1.5} /> {busy ? "Locking…" : "Lock funds"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve dialog */}
      <Dialog open={resolveOpen} onOpenChange={(o) => { if (!o) setResolveOpen(false); }}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Resolve dispute</DialogTitle>
            <DialogDescription>
              As arbiter, choose to release funds to the counterparty or refund the creator.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                variant={resolveDecision === "release" ? "default" : "outline"}
                onClick={() => setResolveDecision("release")}
                data-testid="resolve-release"
                className="rounded-sm flex-1"
              >
                Release to {c.counterparty_legal_name}
              </Button>
              <Button
                variant={resolveDecision === "refund" ? "default" : "outline"}
                onClick={() => setResolveDecision("refund")}
                data-testid="resolve-refund"
                className="rounded-sm flex-1"
              >
                Refund to {c.creator_legal_name}
              </Button>
            </div>
            <Textarea
              data-testid="resolve-note"
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              placeholder="Note (optional, recorded in history)"
              rows={3}
              className="rounded-sm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={onResolve} disabled={busy} data-testid="resolve-confirm" className="rounded-sm">
              <Gavel className="w-3 h-3 mr-1" strokeWidth={1.5} /> Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
